/**
 * Role-Based Access Control (RBAC) Configuration and Utilities
 *
 * This file contains the central configuration for user roles, permissions, and access control
 * throughout the application. It defines which user roles can access which routes and provides
 * utility functions for permission checking.
 *
 * @fileoverview RBAC system configuration and utilities
 * @author Autumn Frontend Team
 * @version 1.0.0
 */

/**
 * Available user roles in the system
 *
 * @typedef {("MarketingOnly" | "U" | "Admin")} UserRole
 *
 * - MarketingOnly: Marketing users with access to marketing analytics only
 * - U: Standard users with full hotel management access
 * - Admin: Administrators with full system access
 */
export type UserRole = "MarketingOnly" | "U" | "Admin";

/**
 * Configuration for a user role
 */
export interface RoleConfig {
  /** Array of routes this role can access */
  allowedRoutes: string[];
  /** Default route to redirect to for this role */
  defaultRoute: string;
  /** Human-readable name for this role */
  name: string;
  /** Optional description of this role's purpose */
  description?: string;
}

/**
 * Configuration for a protected route
 */
export interface RouteConfig {
  /** Array of user roles that can access this route */
  requires: UserRole[];
  /** Optional description of this route's purpose */
  description?: string;
}

/**
 * Main permissions configuration interface
 */
export interface PermissionsConfig {
  /** Configuration for all user roles */
  roles: Record<UserRole, RoleConfig>;
  /** Configuration for all protected routes */
  routes: Record<string, RouteConfig>;
}

/**
 * Centralized permissions configuration
 *
 * This is the main configuration object that defines all user roles and their permissions.
 * To add new roles or modify existing ones, update this configuration.
 *
 * IMPORTANT: When adding new roles, make sure to:
 * 1. Add the role to the UserRole type definition above
 * 2. Add the role configuration here
 * 3. Update any routes that should be accessible to the new role
 * 4. Update the tests in permissions.test.ts
 *
 * @example Adding a new role:
 * // 1. Update UserRole type:
 * export type UserRole = "MarketingOnly" | "U" | "Admin" | "Manager";
 *
 * // 2. Add role configuration:
 * Manager: {
 *   allowedRoutes: ["/dashboard", "/calendar", "/onboarding"],
 *   defaultRoute: "/dashboard",
 *   name: "Manager",
 *   description: "Hotel manager with limited access"
 * }
 *
 * // 3. Update route permissions:
 * "/calendar": {
 *   requires: ["U", "Admin", "Manager"], // Add Manager here
 *   description: "Calendar and pricing management"
 * }
 */
export const PERMISSIONS_CONFIG: PermissionsConfig = {
  roles: {
    MarketingOnly: {
      allowedRoutes: ["/marketing", "/reputation", "/onboarding"],
      defaultRoute: "/marketing",
      name: "Marketing User",
      description: "Access to marketing analytics and insights only",
    },
    U: {
      allowedRoutes: [
        "/dashboard",
        "/calendar",
        "/settings",
        "/ai-reasoning",
        "/reports",
        "/onboarding",
        "/groups",
        "/reputation",
      ],
      defaultRoute: "/dashboard",
      name: "Standard User",
      description: "Full access to hotel management features",
    },
    Admin: {
      allowedRoutes: ["*"], // All routes
      defaultRoute: "/dashboard",
      name: "Administrator",
      description: "Full system access",
    },
  },
  routes: {
    "/dashboard": {
      requires: ["U", "Admin"],
      description: "Main hotel management dashboard",
    },
    "/marketing": {
      requires: ["MarketingOnly", "Admin", "U"],
      description: "Marketing analytics dashboard",
    },
    "/calendar": {
      requires: ["U", "Admin"],
      description: "Calendar and pricing management",
    },
    "/settings": {
      requires: ["U", "Admin"],
      description: "Hotel settings and configuration",
    },
    "/ai-reasoning": {
      requires: ["U", "Admin"],
      description: "AI insights and recommendations",
    },
    "/onboarding": {
      requires: ["MarketingOnly", "U", "Admin"],
      description: "Hotel onboarding process",
    },
    "/booking-engine": {
      requires: ["U", "Admin"],
      description: "Booking engine settings and configuration",
    },
    "/reports": {
      requires: ["U", "Admin"],
      description: "Monthly Business Review reports",
    },
    "/groups": {
      requires: ["U", "Admin"],
      description: "Group booking quote management",
    },
    "/reputation": {
      requires: ["MarketingOnly", "U", "Admin"],
      description: "Guest review management and responses",
    },
  },
};

/**
 * Check if a user role has access to a specific route
 */
export function hasRouteAccess(userRole: UserRole, route: string): boolean {
  const roleConfig = PERMISSIONS_CONFIG.roles[userRole];
  const routeConfig = PERMISSIONS_CONFIG.routes[route];

  // Admin has access to all routes
  if (userRole === "Admin") {
    return true;
  }

  // Check if route exists in configuration
  if (!routeConfig) {
    console.warn(`Route ${route} not found in permissions configuration`);
    return false;
  }

  // Check if user role is allowed for this route
  return (
    routeConfig.requires.includes(userRole) ||
    routeConfig.requires.includes("*" as UserRole)
  );
}

/**
 * Get the default route for a user role
 */
export function getDefaultRoute(userRole: UserRole): string {
  const roleConfig = PERMISSIONS_CONFIG.roles[userRole];
  return roleConfig?.defaultRoute || "/dashboard";
}

/**
 * Get all allowed routes for a user role
 */
export function getAllowedRoutes(userRole: UserRole): string[] {
  const roleConfig = PERMISSIONS_CONFIG.roles[userRole];

  if (userRole === "Admin") {
    // Admin has access to all defined routes
    return Object.keys(PERMISSIONS_CONFIG.routes);
  }

  return roleConfig?.allowedRoutes || [];
}

/**
 * Normalize backend role to frontend UserRole
 * Maps backend role codes to frontend role names
 */
export function normalizeUserRole(backendRole: string): UserRole | null {
  // Map backend role codes to frontend roles
  const roleMap: Record<string, UserRole> = {
    'A': 'Admin',
    'Admin': 'Admin',
    'U': 'U',
    'MarketingOnly': 'MarketingOnly',
  };

  return roleMap[backendRole] || null;
}

/**
 * Check if a user role exists in the system
 */
export function isValidUserRole(role: string): role is UserRole {
  return role in PERMISSIONS_CONFIG.roles;
}

/**
 * The slice of the persisted session (`localStorage.user`) that routing reads.
 *
 * `type` is the role. `pricingDashboardHidden` is the backend's per-account
 * override for a full-platform customer who never uses pricing (Hotel
 * Metropole): it rides the login / session / profile payloads next to the
 * other product flags. A blob written before the flag existed simply lacks
 * it, which reads as "not hidden" — the role's normal behavior.
 */
export interface RoutingUser {
  type?: string | null;
  pricingDashboardHidden?: boolean;
}

/**
 * Routes the account-level override removes on top of the role's grant. Only
 * the pricing dashboard today; Calendar and the rest stay as the role allows.
 */
export function isRouteHiddenForUser(
  user: RoutingUser | null | undefined,
  route: string
): boolean {
  return user?.pricingDashboardHidden === true && route === "/dashboard";
}

/**
 * Role grant minus the account-level override. A missing or unknown role is
 * never granted anything, mirroring hasRouteAccess's unknown-route stance.
 */
export function hasRouteAccessForUser(
  user: RoutingUser | null | undefined,
  route: string
): boolean {
  const role = user?.type;
  if (!role || !isValidUserRole(role)) return false;
  if (isRouteHiddenForUser(user, route)) return false;
  return hasRouteAccess(role, route);
}

/**
 * Where this account lands after login / on "/": the role's default route,
 * unless the override hides it — then Marketing, which every role can reach.
 */
export function getDefaultRouteForUser(
  user: RoutingUser | null | undefined
): string {
  const roleDefault = getDefaultRoute(user?.type as UserRole);
  return isRouteHiddenForUser(user, roleDefault) ? "/marketing" : roleDefault;
}

/**
 * Get user role information
 */
export function getRoleInfo(userRole: UserRole): RoleConfig | null {
  return PERMISSIONS_CONFIG.roles[userRole] || null;
}

/**
 * Get all available roles
 */
export function getAllRoles(): UserRole[] {
  return Object.keys(PERMISSIONS_CONFIG.roles) as UserRole[];
}

/**
 * Check if a route requires authentication
 */
export function isProtectedRoute(route: string): boolean {
  return route in PERMISSIONS_CONFIG.routes;
}

/**
 * Get routes that require specific roles
 */
export function getRoutesForRole(userRole: UserRole): string[] {
  const allowedRoutes: string[] = [];

  Object.entries(PERMISSIONS_CONFIG.routes).forEach(([route, config]) => {
    if (config.requires.includes(userRole) || userRole === "Admin") {
      allowedRoutes.push(route);
    }
  });

  return allowedRoutes;
}

/**
 * Validate user permissions for navigation
 */
export function validateNavigation(
  userRole: UserRole,
  targetRoute: string
): {
  allowed: boolean;
  defaultRoute: string;
  reason?: string;
} {
  const allowed = hasRouteAccess(userRole, targetRoute);
  const defaultRoute = getDefaultRoute(userRole);

  if (!allowed) {
    return {
      allowed: false,
      defaultRoute,
      reason: `User role '${userRole}' does not have access to route '${targetRoute}'`,
    };
  }

  return {
    allowed: true,
    defaultRoute,
  };
}
